// Shared formatting/taxonomy helpers for the Calculation Results view. Ported
// from reporting-hub/frontend/src/lib/format.ts + the CALCULATION_CATEGORIES
// constant in reporting-hub/frontend/src/lib/types.ts - kept as plain JS
// functions/constants (no TS types) since exaport is a JS codebase. Not one
// of the components explicitly listed in the brief, but factored out here
// because CalculationTable/VariousStatsPanel/CashPositionsMatrix/
// NavCalculationMatrix/GroupPanel/CategorySidebar all need the same value
// formatting + platform ordering/labeling.

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const PLATFORM_ORDER = ['FINBEE', 'VAURAUS', 'SAVY', 'MOZZENO'];

// Compartment each platform's loans/notes are issued through - see
// reporting-hub/frontend/src/lib/format.ts's PLATFORM_COMPARTMENTS (same
// values, appended to the platform name so it reads unambiguously next to
// the fund's own compartments, e.g. "Finbee (O1012)").
const PLATFORM_COMPARTMENTS = {
  FINBEE: 'O1012',
  VAURAUS: 'E165',
  SAVY: 'E190',
  MOZZENO: 'O1105',
};

export const titleCasePlatform = (value) => {
  if (!value) return value;
  const label = value.charAt(0) + value.slice(1).toLowerCase();
  const compartment = PLATFORM_COMPARTMENTS[value.toUpperCase()];
  return compartment ? `${label} (${compartment})` : label;
};

export const formatMonth = (value) => {
  const match = /^(\d{4})-(\d{2})$/.exec(value || '');
  if (!match) return value;
  const [, year, month] = match;
  const label = MONTH_NAMES[Number(month) - 1];
  return label ? `${label} ${year}` : value;
};

export const formatDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!match) return value;
  const [, year, month, day] = match;
  const label = MONTH_NAMES[Number(month) - 1];
  return label ? `${Number(day)} ${label} ${year}` : value;
};

export const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(value)}, ${timePart}`;
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${(Number(value) * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPlain = (value) => {
  if (value === null || value === undefined) return '–';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

// Formats a calculation-table cell according to its column's `format` hint
// (see calculations.py column defs: "month" | "date" | "currency" | "percent"
// | "number" - anything else falls through to plain string rendering).
export const formatCalcValue = (value, format) => {
  if (value === null || value === undefined) return '–';
  switch (format) {
    case 'month':
      return typeof value === 'string' ? formatMonth(value) : formatPlain(value);
    case 'date':
      return typeof value === 'string' ? formatDate(value) : formatPlain(value);
    case 'currency':
      return typeof value === 'number' ? formatCurrency(value) : formatPlain(value);
    case 'percent':
      return typeof value === 'number' ? formatPercent(value) : formatPlain(value);
    case 'number':
      return typeof value === 'number' ? formatNumber(value) : formatPlain(value);
    default:
      return formatPlain(value);
  }
};

export const isNumericFormat = (format) => format === 'currency' || format === 'percent' || format === 'number';

// PreflightSettings' subscriptions[].date / interest_distributions[].date /
// issue_dates[platform] are pydantic `date` fields - serialized over JSON as
// plain "YYYY-MM-DD" strings, not datetimes. PrimeReact's Calendar wants a JS
// Date for `value` - these two converters round-trip without a timezone
// shift (constructing via local Y/M/D components rather than parsing the ISO
// string directly, which `new Date("YYYY-MM-DD")` would interpret as UTC
// midnight and can render as the previous day in timezones behind UTC).
export const isoToDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

export const dateToIso = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Builds a single-line tooltip string for a table cell: the formula (row-
// specific override from row._explain[col.id].formula, falling back to the
// column's static `formula`) plus any explain components (label/value pairs),
// joined with " | " rather than newlines - PrimeReact's default Tooltip
// content div doesn't preserve whitespace, so newlines would just collapse.
export const buildCellTooltip = (column, row) => {
  const rowExplain = row && row._explain;
  const explain = rowExplain ? rowExplain[column.id] : undefined;
  const formula = (explain && explain.formula) || column.formula;
  const components = (explain && explain.components) || [];
  if (!formula && components.length === 0) return null;
  const parts = [];
  if (formula) parts.push(formula);
  components.forEach((c) => parts.push(`${c.label}: ${formatCalcValue(c.value, c.format)}`));
  return parts.join('  |  ');
};

export const isCellHighlighted = (column, row) => {
  const rowExplain = row && row._explain;
  const explain = rowExplain ? rowExplain[column.id] : undefined;
  return !!(explain && explain.highlight);
};

// The 15 groups the calc service currently produces, organized into the 4
// dashboard categories used by the Calculation Results UI - mirrors
// reporting-hub/frontend/src/lib/types.ts's CALCULATION_CATEGORIES exactly
// (client-side IA grouping, not derived from the backend). Groups not
// present in a given calculation result (e.g. not-yet-implemented ones) are
// filtered out by the caller, not here.
export const CALCULATION_CATEGORIES = [
  {
    id: 'overview',
    label: 'Overview',
    groups: ['general_overview', 'nav_calculation', 'monthly_stats', 'various_stats', 'outstanding_volume_daily'],
  },
  {
    id: 'allocations',
    label: 'Allocations',
    groups: ['asset_allocation', 'allocation_security_type', 'allocation_country', 'allocation_credit_score'],
  },
  {
    id: 'distributions',
    label: 'Distributions',
    groups: ['term_distribution', 'interest_rate_distribution', 'days_late_distribution'],
  },
  {
    id: 'cash_and_fees',
    label: 'Cash & Fees',
    groups: ['cash_positions', 'cost_calculation', 'interest_overview'],
  },
];
